import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { queryClient } from 'services/reactQueryService';
import _ from 'lodash';

interface FetchLenseConfigProp extends HookInputProps {}

function useFetchAttributeNamesV2({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    extras,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [count, setCount] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const variableRef = useRef({});
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setError(null);
            }
            variableRef.current = variables;
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.response?.data?.detail || error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) {
                setError(null);
            }
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const {
                dimension,
                allAttributeNames,
                allSelectedValueForAllAttribute,
                setAllCheckedAttribute,
                allCheckedAttribute,
                setAllAttributeNames,
                applyFilterObject,
            } = extras;
            const data = payload?.data;
            let mergedData: any[] = [];
            if (payload && Array.isArray(data)) {
                if (data?.length === 0) throw 'No Data';
                const parsedData = data?.map((item: any) => {
                    return {
                        attributeExId: item?.attribute_name,
                        attributeDesc: item?.attribute_name,
                        attributeType: variables?.attributeType,
                        isActive: item?.is_active,
                        // count:item?.count,
                        dimension: dimension,
                    };
                });

                //inf scroll
                if (variables?.offset > 0) {
                    mergedData = [...(allAttributeNames[variables?.attributeType] ?? []), ...parsedData];
                } else {
                    mergedData = parsedData;
                }

                //set attr names state
                setAllAttributeNames((oldData: any) => {
                    const data = { ...oldData };
                    data[variables?.attributeType] = mergedData;
                    return data;
                });

                //if select all checkbox is selected
                if (allSelectedValueForAllAttribute[variables?.attributeType]) {
                    //exclusion list
                    const exclusionListForCurrentAttrType =
                        applyFilterObject?.[dimension]?.[variables?.attributeType]?.exclusion;
                    //set all checkbox state, this state is used to track checkbox selection
                    //check checkbox
                    setAllCheckedAttribute((oldData: any) => {
                        const data = { ...oldData };
                        data[variables?.attributeType] = {};
                        mergedData?.forEach((attrName: any) => {
                            //if attribute Name is active then only set
                            if (attrName.isActive) {
                                data[variables?.attributeType][attrName.attributeExId] = attrName;
                            }
                        });
                        //remove the excluded attributes (uncheck checkbox)
                        exclusionListForCurrentAttrType?.forEach((exclusionAttr: any) => {
                            delete data[variables?.attributeType][exclusionAttr.attributeExId];
                        });
                        return data;
                    });
                    //if checkbox is already checked, then compare those with new attribute names
                } else if (
                    Object.keys(allCheckedAttribute?.[variables?.attributeType] ?? {}).length > 0 &&
                    variables?.offset + variables?.limit > (payload?.is_active_count ?? 0) && //checking if more items are present
                    !variables?.searchKeyword //checking if search is present
                ) {
                    // setAllCheckedAttribute((oldData: any) => {
                    //     const data = _.cloneDeep(oldData);
                    //     data[variables?.attributeType] = {};
                    //     mergedData?.forEach((attrName: any) => {
                    //         //if attribute Name is active then only set
                    //         if (attrName.attributeExId in oldData[variables?.attributeType] && attrName.isActive) {
                    //             data[variables?.attributeType][attrName.attributeExId] = attrName;
                    //         }
                    //     });
                    //     return data;
                    // });
                }
            }
            setCount(payload?.total_count ?? 0);
            setActiveCount(payload?.is_active_count ?? 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        const { setAllAttributeNames } = extras;
        //reset variables on error
        //set attr names state
        setAllAttributeNames((oldData: any) => {
            const data = { ...oldData };
            data[variables?.attributeType] = [];
            return data;
        });
        setLoading(false);
        setError(error);
        setActiveCount(0);
        setCount(0);
    }

    interface AttributeNamesOutputType extends outputType {
        isActiveCount: number;
    }

    const outputData: AttributeNamesOutputType = { error, loading, count, isActiveCount: activeCount };
    return outputData;
}

export { useFetchAttributeNamesV2 };
